import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { cn } from "@/lib/utils";
import { docsArticles } from "@/data/docsArticles";
import { docsContent } from "@/data/docsContent";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export default function Documentation() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const activeArticleId = articleId || docsArticles[0]?.id;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const activeArticle = docsArticles.find((a) => a.id === activeArticleId);
  const content = activeArticleId ? docsContent[activeArticleId] : null;

  const handleArticleClick = (id: string) => {
    navigate(`/docs/${id}`);
    setMobileNavOpen(false);
  };

  // Group articles by category
  const categories = Array.from(new Set(docsArticles.map((a) => a.category)));

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{activeArticle ? `${activeArticle.title} — HostCaptain Docs` : "Documentation — HostCaptain"}</title>
        <meta name="description" content="HostCaptain documentation — learn about the platform, our approach, features, and more." />
        <link rel="canonical" href={`https://hostcaptain.co/docs${activeArticleId ? `/${activeArticleId}` : ""}`} />
      </Helmet>
      <Navbar isAuthenticated={isAuthenticated} onLogout={handleLogout} />

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-64 shrink-0 border-r border-border bg-card min-h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto">
          <nav className="p-4 space-y-6">
            {categories.map((cat) => (
              <div key={cat}>
                <p className="px-3 mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                  {cat}
                </p>
                <div className="space-y-1">
                  {docsArticles
                    .filter((a) => a.category === cat)
                    .map((article) => (
                      <button
                        key={article.id}
                        onClick={() => handleArticleClick(article.id)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                          activeArticleId === article.id
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {article.title}
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile nav toggle */}
        <div className="md:hidden fixed bottom-6 left-6 z-50">
          <Button
            size="icon"
            className="h-12 w-12 rounded-full shadow-lg"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
          >
            {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile sidebar overlay */}
        {mobileNavOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)}>
            <aside className="w-64 bg-card border-r border-border h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <nav className="p-4 space-y-6 pt-6">
                {categories.map((cat) => (
                  <div key={cat}>
                    <p className="px-3 mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                      {cat}
                    </p>
                    <div className="space-y-1">
                      {docsArticles
                        .filter((a) => a.category === cat)
                        .map((article) => (
                          <button
                            key={article.id}
                            onClick={() => handleArticleClick(article.id)}
                            className={cn(
                              "w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                              activeArticleId === article.id
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            {article.title}
                          </button>
                        ))}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 px-6 py-10 md:px-12 lg:px-20 max-w-4xl">
          {content || (
            <div className="text-muted-foreground text-center py-20">
              Select an article from the sidebar.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
